import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { AppShellV8 } from "../AppShellV8";
import { getActiveClient, useActiveClient, getCurrentMemberId } from "../activeClient";
import { personFor } from "../people";
import { useRoster, useEnsureRoster } from "../roster";
import { PersonFilter } from "../PersonFilter";

/**
 * Calendar — real schedule built from the operator API: meetings (by date),
 * deliverables (by due date → Deadline, or Review Due when in review), campaign
 * phase boundaries (→ Milestones), and open to-dos (→ Tasks Due, by due date).
 *
 * Round 2 (D-7/D-9): a "Tasks Due" panel rides in the day sidebar alongside Events,
 * an in-screen person filter (default = the signed-in member) scopes both, the Week
 * view is an Apple-style time-grid (7 day-columns × hours + an all-day strip), and
 * the Agenda view is a grouped Apple-style list. Built to the signed-off prototype
 * (PROTOTYPE-round2-calendar.html). Client scope stays on the global switcher (GD-8).
 */

type ViewMode = "month" | "week" | "agenda";
type EventType = "meeting" | "deadline" | "review" | "milestone";

interface CalEvent {
  id: string;
  title: string;
  date: string; // ISO yyyy-mm-dd
  type: EventType;
  time?: string; // display label ("9:00 AM", "EOD")
  hour?: number; // 0–23 for timed meetings; undefined → all-day
  client: string;
  owner?: string; // raw owner string when known (deliverable deadlines/reviews); meetings/milestones have none
  href: string;
}

interface CalTask {
  id: string;
  title: string;
  date: string; // ISO yyyy-mm-dd (only tasks with a parseable due date land on the calendar)
  owner: string;
  difficulty: number; // 1–5
  client: string | null;
  href: string;
}

const TYPE_CONFIG: Record<EventType, { label: string; dot: string; chip: string }> = {
  meeting: { label: "Meeting", dot: "bg-blue-500", chip: "bg-blue-50 text-blue-700" },
  deadline: { label: "Deadline", dot: "bg-red-500", chip: "bg-red-50 text-red-700" },
  review: { label: "Review Due", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700" },
  milestone: { label: "Milestone", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
};

const _ac = getActiveClient();
const CLIENT_CONFIG: Record<string, { name: string; short: string; dot: string }> = {
  [_ac.slug]: { name: _ac.name, short: _ac.initials, dot: _ac.color },
  internal: { name: "Internal", short: "INT", dot: "bg-gray-400" },
};
const clientCfg = (slug: string) => CLIENT_CONFIG[slug] || { name: slug, short: slug.slice(0, 2).toUpperCase(), dot: "bg-gray-400" };

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TYPE_ORDER: EventType[] = ["meeting", "deadline", "review", "milestone"];
const GRID_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];

const pad = (n: number) => String(n).padStart(2, "0");
const isoFor = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
const isoOf = (dt: Date) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
const prettify = (s?: string | null) => (s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtTime = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};
const time12 = (hour: number) => {
  const ap = hour >= 12 ? "PM" : "AM";
  const h = hour % 12 || 12;
  return `${h}:00 ${ap}`;
};
// Only a due value that resolves to a real calendar date can be placed on a day; free-text
// dues ("Today", "This week") are skipped (they can't be positioned).
const isoDate = (due?: string | null): string | null => {
  if (!due) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(due));
  return m ? m[1] : null;
};
// A to-do's due is usually free text ("Today", "This week") rather than an ISO date,
// so a strict ISO parse dropped nearly every task off the calendar. Resolve the common
// relative terms to a date, anchored to the to-do's MEETING date (so "Today"/"Tomorrow"
// mean the meeting day, not the day you happen to view the calendar). Truly unresolvable
// dues return null (they can't be placed on a day).
const meetingAnchor = (mid?: string | null): Date => {
  const m = /(\d{4}-\d{2}-\d{2})/.exec(String(mid || ""));
  return m ? new Date(`${m[1]}T00:00:00`) : new Date();
};
const resolveTaskDue = (due: string | null | undefined, sourceMeeting?: string | null): string | null => {
  if (!due) return null;
  const isoM = /^(\d{4}-\d{2}-\d{2})/.exec(String(due));
  if (isoM) return isoM[1];
  const s = String(due).toLowerCase();
  const d = meetingAnchor(sourceMeeting);
  if (/\btoday\b/.test(s)) return isoOf(d); // "today", "today / tomorrow", "today / post-call"
  if (/\btomorrow\b/.test(s)) { d.setDate(d.getDate() + 1); return isoOf(d); }
  if (/this week|end of week|by (mon|tue|wed|thu|fri)/.test(s)) {
    d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7)); // the meeting week's Friday
    return isoOf(d);
  }
  return null;
};
const normKey = (o?: string | null) => (o || "").trim().toLowerCase();

// Difficulty → color band (D-4: 1-2 green, 3 amber, 4-5 red).
const diffBand = (d: number) =>
  d <= 2 ? { fill: "bg-green-500", light: "bg-green-100" } : d <= 3 ? { fill: "bg-amber-500", light: "bg-amber-100" } : { fill: "bg-red-500", light: "bg-red-100" };

function DiffBars({ d }: { d: number }) {
  const b = diffBand(d);
  return (
    <span className="inline-flex gap-0.5 align-middle">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`h-1.5 w-3 rounded-full ${i < d ? b.fill : b.light}`} />
      ))}
    </span>
  );
}

function OwnerAvatar({ owner, sz = "h-6 w-6 text-[9px]" }: { owner?: string | null; sz?: string }) {
  const p = personFor(owner);
  return <span className={`flex ${sz} shrink-0 items-center justify-center rounded-full ${p.color} font-bold text-white`}>{p.initials}</span>;
}

export function Calendar() {
  // Reactive active-client read (AMB-333 / GD-8): the calendar follows the global
  // workspace client switcher in the chrome — no in-screen client filter.
  const slug = useActiveClient().slug;
  useEnsureRoster();
  const roster = useRoster(); // this tenant's own members (AMB-438) — for the person filter

  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [view, setView] = useState<ViewMode>("month");
  const [selectedDay, setSelectedDay] = useState(now.getDate());
  const [person, setPerson] = useState<string>(() => getCurrentMemberId() ?? "all"); // default = signed-in member; "all" (Everyone) when the login isn't a team member
  const [activeTypes, setActiveTypes] = useState<Set<EventType>>(new Set(TYPE_ORDER));
  const [activeEvent, setActiveEvent] = useState<CalEvent | null>(null);
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [tasks, setTasks] = useState<CalTask[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const base = `${DOCS_API_DOMAIN}/api/agents`;
    Promise.all([
      fetch(`${base}/meetings?client=${slug}`).then((r) => (r.ok ? r.json() : { items: [] })),
      fetch(`${base}/deliverables?client=${slug}`).then((r) => (r.ok ? r.json() : { items: [] })),
      fetch(`${base}/campaigns?client=${slug}`).then((r) => (r.ok ? r.json() : { items: [] })),
      fetch(`${base}/actions?client=${slug}`).then((r) => (r.ok ? r.json() : { items: [] })),
    ])
      .then(([meetings, dels, camps, actions]) => {
        if (cancelled) return;
        const out: CalEvent[] = [];
        for (const m of meetings.items || []) {
          if (!m.date) continue;
          const dt = new Date(m.date);
          out.push({
            id: `m-${m.id}`,
            title: m.title,
            date: String(m.date).slice(0, 10),
            type: "meeting",
            time: fmtTime(m.date),
            hour: isNaN(dt.getTime()) ? undefined : dt.getHours(),
            client: m.client || "internal",
            href: `/agents/digest?id=${encodeURIComponent(m.id)}&from=calendar`,
          });
        }
        for (const d of dels.items || []) {
          const date = isoDate(d.due);
          if (!date) continue;
          const inReview = d.reviewState === "in-review" || d.reviewState === "changes-requested";
          out.push({
            id: `d-${d.id}`,
            title: d.title,
            date,
            type: inReview ? "review" : "deadline",
            time: "EOD",
            client: d.client || slug,
            owner: d.owner || undefined,
            href: `/agents/transcript-view?id=${encodeURIComponent(d.id)}`,
          });
        }
        for (const c of camps.items || []) {
          for (const ph of c.phases || []) {
            const start = ph.start || ph.startDate;
            const date = isoDate(start);
            if (!date) continue;
            out.push({
              id: `p-${c.id}-${ph.id || ph.name}`,
              title: `${prettify(c.name)}: ${ph.name || ph.id} starts`,
              date,
              type: "milestone",
              client: c.client || slug,
              href: `/agents/campaigns?campaign=${encodeURIComponent(c.id)}`,
            });
          }
        }
        setEvents(out);

        const tout: CalTask[] = [];
        for (const a of actions.items || []) {
          const date = resolveTaskDue(a.due, a.sourceMeeting);
          if (!date) continue; // only to-dos whose due resolves to a date can sit on the calendar
          tout.push({
            id: `t-${a.id}`,
            title: a.task,
            date,
            owner: a.owner || "",
            difficulty: typeof a.difficulty === "number" ? a.difficulty : 2,
            client: a.client || null,
            // AMB-408/410: a calendar to-do opens the TASK in the Tasks app (deep-linked),
            // not its source-meeting digest (which could be missing → broken link).
            href: `/agents/tasks?open=${encodeURIComponent(a.id)}`,
          });
        }
        setTasks(tout);
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([]);
          setTasks([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const firstOffset = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const todayIso = isoOf(now);
  const todayDay = now.getFullYear() === cursor.y && now.getMonth() === cursor.m ? now.getDate() : -1;

  // Person filter (D-7): scopes both events and tasks. Ownerless events (shared
  // meetings / milestones) stay visible under a person filter; owned events
  // (deliverable deadlines/reviews) and tasks filter to the selected person.
  const eventVisible = (e: CalEvent) => activeTypes.has(e.type) && (person === "all" || !e.owner || normKey(e.owner) === person);
  const taskVisible = (t: CalTask) => person === "all" || normKey(t.owner) === person;
  const visibleEvents = useMemo(() => events.filter(eventVisible), [events, activeTypes, person]);
  const visibleTasks = useMemo(() => tasks.filter(taskVisible), [tasks, person]);

  const eventsOn = (iso: string) => visibleEvents.filter((e) => e.date === iso);
  const tasksOn = (iso: string) => visibleTasks.filter((t) => t.date === iso);

  // The 7 dates of the selected day's week (Sun→Sat), cross-month aware.
  const weekDates = useMemo(() => {
    const sel = new Date(cursor.y, cursor.m, selectedDay);
    const start = new Date(sel);
    start.setDate(sel.getDate() - sel.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(start);
      dt.setDate(start.getDate() + i);
      return { iso: isoOf(dt), dow: i, dom: dt.getDate(), label: `${MONTHS[dt.getMonth()].slice(0, 3)} ${dt.getDate()}` };
    });
  }, [cursor, selectedDay]);

  const monthPrefix = `${cursor.y}-${pad(cursor.m + 1)}-`;
  const selectedIso = isoFor(cursor.y, cursor.m, selectedDay);

  const shiftMonth = (delta: number) => {
    setCursor((c) => {
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
    setSelectedDay(1);
  };

  const toggleType = (t: EventType) =>
    setActiveTypes((prev) => {
      const n = new Set(prev);
      n.has(t) ? n.delete(t) : n.add(t);
      return n;
    });

  if (loading) {
    return (
      <AppShellV8 breadcrumb={["Calendar"]}>
        <div className="flex h-full items-center justify-center">
          <p className="text-[13px] text-gray-400">Loading your calendar…</p>
        </div>
      </AppShellV8>
    );
  }

  return (
    <AppShellV8 breadcrumb={["Calendar"]}>
      <div className="flex h-full flex-col">
        {/* Header toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b border-black/[0.06] px-5 py-3">
          <h1 className="text-base font-semibold tracking-tight text-gray-900">{MONTHS[cursor.m]} {cursor.y}</h1>
          <div className="flex items-center gap-1">
            <button onClick={() => shiftMonth(-1)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </button>
            <button onClick={() => shiftMonth(1)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
            </button>
            <button onClick={() => { setCursor({ y: now.getFullYear(), m: now.getMonth() }); setSelectedDay(now.getDate()); }} className="ml-1 rounded-lg border border-black/[0.06] px-2.5 py-1 text-xs font-medium text-gray-500 transition-all duration-150 hover:bg-gray-50">Today</button>
          </div>
          <div className="flex rounded-lg border border-black/[0.06] p-0.5">
            {(["month", "week", "agenda"] as ViewMode[]).map((v) => (
              <button key={v} onClick={() => setView(v)} className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-all duration-150 ${view === v ? "bg-gray-900 text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>{v}</button>
            ))}
          </div>

          {/* Person filter (D-7, AMB-387) — shared component + consistent label; defaults to
              the active user, scopes events + tasks. Client scope stays on the global switcher. */}
          <PersonFilter
            className="ml-auto"
            value={person}
            onChange={setPerson}
            options={roster.map((m) => ({ id: m.id, name: m.name }))}
          />
        </div>

        {/* Legend = live filter */}
        <div className="flex flex-wrap items-center gap-2 border-b border-black/[0.06] px-5 py-2">
          <span className="text-[11px] font-medium text-gray-400">Event type</span>
          {TYPE_ORDER.map((t) => {
            const on = activeTypes.has(t);
            return (
              <button key={t} onClick={() => toggleType(t)} className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-all duration-150 ${on ? "bg-gray-100 text-gray-700 ring-1 ring-black/[0.06]" : "text-gray-400 line-through hover:text-gray-500"}`}>
                <span className={`h-2 w-2 rounded-full ${on ? TYPE_CONFIG[t].dot : "bg-gray-300"}`} />
                {TYPE_CONFIG[t].label}
              </button>
            );
          })}
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
            <span className="h-2.5 w-2.5 rounded-[3px] bg-gray-300 ring-1 ring-gray-400" />Task (to-do)
          </span>
        </div>

        {/* Month */}
        {view === "month" && (
          <div className="flex flex-1 overflow-hidden">
            <div className="flex flex-1 flex-col">
              <div className="grid grid-cols-7 border-b border-black/[0.06]">
                {WEEKDAYS.map((d) => (<div key={d} className="px-2 py-1.5 text-[10px] font-semibold tracking-wider text-gray-400">{d}</div>))}
              </div>
              <div className="grid flex-1 grid-cols-7 grid-rows-6">
                {Array.from({ length: 42 }).map((_, i) => {
                  const day = i - firstOffset + 1;
                  const valid = day >= 1 && day <= daysInMonth;
                  const iso = isoFor(cursor.y, cursor.m, day);
                  const dayEvents = valid ? eventsOn(iso) : [];
                  const dayTaskCount = valid ? tasksOn(iso).length : 0;
                  const isToday = day === todayDay;
                  const isSelected = day === selectedDay;
                  return (
                    <div key={i} onClick={() => valid && setSelectedDay(day)} className={`flex cursor-default flex-col gap-0.5 border-b border-r border-black/[0.06] p-1.5 text-left transition-all duration-150 ${valid ? "hover:bg-gray-50/60" : "bg-gray-50/30"} ${valid && isSelected ? "bg-blue-50/60 ring-1 ring-inset ring-blue-200" : ""}`}>
                      {valid && (
                        <>
                          <span className={`text-xs ${isToday ? "flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 font-semibold text-white" : "text-gray-600"}`}>{day}</span>
                          <div className="flex flex-col gap-0.5">
                            {dayEvents.slice(0, 3).map((e) => (
                              <button key={e.id} onClick={(ev) => { ev.stopPropagation(); setActiveEvent(e); }} className="group flex w-full items-center gap-1 truncate rounded py-0.5 pr-1 text-[10px] transition-all duration-150 hover:bg-gray-100">
                                <span className={`h-3.5 w-[3px] shrink-0 rounded-full ${TYPE_CONFIG[e.type].dot}`} />
                                <span className="truncate text-gray-700">{e.title}</span>
                              </button>
                            ))}
                            {dayEvents.length > 3 && <span className="pl-1 text-[10px] text-gray-400">+{dayEvents.length - 3} more</span>}
                            {dayTaskCount > 0 && <span className="pl-1 text-[10px] font-medium text-gray-400">{dayTaskCount} task{dayTaskCount > 1 ? "s" : ""} due</span>}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <DayDetail iso={selectedIso} events={eventsOn(selectedIso)} tasks={tasksOn(selectedIso)} onEvent={setActiveEvent} />
          </div>
        )}

        {/* Week — Apple-style time grid (7 day-columns × hours + all-day strip) */}
        {view === "week" && (
          <div className="flex flex-1 overflow-hidden">
            <div className="flex-1 overflow-auto">
              <WeekGrid weekDates={weekDates} todayIso={todayIso} selectedIso={selectedIso} eventsOn={eventsOn} tasksOn={tasksOn} onEvent={setActiveEvent} onPick={(iso) => { const d = new Date(`${iso}T00:00:00`); setCursor({ y: d.getFullYear(), m: d.getMonth() }); setSelectedDay(d.getDate()); }} />
            </div>
            <DayDetail iso={selectedIso} events={eventsOn(selectedIso)} tasks={tasksOn(selectedIso)} onEvent={setActiveEvent} />
          </div>
        )}

        {/* Agenda — Apple-style grouped list (events + tasks inline) */}
        {view === "agenda" && (
          <div className="flex flex-1 overflow-hidden">
            <div className="flex-1 overflow-auto p-5">
              <AgendaView monthPrefix={monthPrefix} monthLabel={`${MONTHS[cursor.m]} ${cursor.y}`} events={visibleEvents} tasks={visibleTasks} onEvent={setActiveEvent} />
            </div>
            <DayDetail iso={selectedIso} events={eventsOn(selectedIso)} tasks={tasksOn(selectedIso)} onEvent={setActiveEvent} />
          </div>
        )}
      </div>

      {/* Event detail popover */}
      {activeEvent && (
        <>
          <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px]" onClick={() => setActiveEvent(null)} />
          <div className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-5 shadow-2xl ring-1 ring-black/[0.06]">
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900">{activeEvent.title}</h3>
              <button onClick={() => setActiveEvent(null)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="space-y-2 text-[13px]">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${TYPE_CONFIG[activeEvent.type].dot}`} />
                <span className="text-gray-500">{TYPE_CONFIG[activeEvent.type].label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${clientCfg(activeEvent.client).dot}`} />
                <span className="text-gray-500">{clientCfg(activeEvent.client).name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {new Date(`${activeEvent.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}{activeEvent.time ? ` · ${activeEvent.time}` : ""}
              </div>
            </div>
            <Link to={activeEvent.href} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-[13px] font-medium text-white transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">
              {activeEvent.type === "meeting" ? "Open meeting digest" : activeEvent.type === "milestone" ? "Open campaign" : "Open document"}
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
            </Link>
          </div>
        </>
      )}
    </AppShellV8>
  );
}

// Day sidebar — Events above, Tasks Due below (D-7). Shared by Month / Week / Agenda.
function DayDetail({ iso, events, tasks, onEvent }: { iso: string; events: CalEvent[]; tasks: CalTask[]; onEvent: (e: CalEvent) => void }) {
  const d = new Date(`${iso}T00:00:00`);
  const label = isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric" });
  return (
    <div className="w-72 shrink-0 overflow-auto border-l border-black/[0.06] bg-gray-50/60">
      <div className="border-b border-black/[0.06] px-5 py-4">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Selected Day</p>
        <h2 className="mt-0.5 text-base font-semibold text-gray-900">{label}</h2>
      </div>
      <div className="px-5 py-4">
        <p className="mb-3 text-[13px] font-semibold text-gray-900">Events</p>
        <div className="space-y-2">
          {events.length === 0 && <p className="text-xs text-gray-400">No events</p>}
          {events.map((e) => (
            <button key={e.id} onClick={() => onEvent(e)} className="w-full rounded-lg bg-white p-3 text-left shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:shadow">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${TYPE_CONFIG[e.type].dot}`} />
                <span className="text-xs font-medium text-gray-500">{e.time || TYPE_CONFIG[e.type].label}</span>
              </div>
              <p className="mt-1 text-[13px] font-medium text-gray-900">{e.title}</p>
              {e.client && e.client !== "internal" && <span className="mt-1.5 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">{clientCfg(e.client).name}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="border-t border-black/[0.06] px-5 py-4">
        <p className="mb-3 text-[13px] font-semibold text-gray-900">Tasks Due</p>
        <div className="space-y-2">
          {tasks.length === 0 && <p className="text-xs text-gray-400">No tasks due</p>}
          {tasks.map((t) => {
            const card = (
              // D-9: the title is truncated, so hover shows the full task text as a tooltip.
              <div title={t.title} className="flex items-center gap-2.5 rounded-lg bg-white p-3 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:shadow">
                <OwnerAvatar owner={t.owner} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-gray-900">{t.title}</p>
                  <div className="mt-1"><DiffBars d={t.difficulty} /></div>
                </div>
              </div>
            );
            return t.href ? <Link key={t.id} to={t.href} className="block">{card}</Link> : <div key={t.id}>{card}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

// Week — Apple-style time grid: 7 day-columns (Sun→Sat) × hour rows, plus an all-day
// strip for untimed deadlines/reviews/milestones + tasks (per the prototype, layout A).
function WeekGrid({
  weekDates, todayIso, selectedIso, eventsOn, tasksOn, onEvent, onPick,
}: {
  weekDates: { iso: string; dow: number; dom: number; label: string }[];
  todayIso: string;
  selectedIso: string;
  eventsOn: (iso: string) => CalEvent[];
  tasksOn: (iso: string) => CalTask[];
  onEvent: (e: CalEvent) => void;
  onPick: (iso: string) => void;
}) {
  const cols = "56px repeat(7,1fr)";
  const inGrid = (e: CalEvent) => e.hour !== undefined && e.hour >= GRID_HOURS[0] && e.hour <= GRID_HOURS[GRID_HOURS.length - 1];
  return (
    <div className="px-4 py-3">
      {/* Day headers */}
      <div className="grid" style={{ gridTemplateColumns: cols }}>
        <div />
        {weekDates.map((w) => {
          const today = w.iso === todayIso;
          return (
            <button key={w.iso} onClick={() => onPick(w.iso)} className={`border-l border-black/[0.06] py-2 text-center ${w.iso === selectedIso ? "bg-blue-50/50" : ""}`}>
              <div className={`text-[10px] font-semibold uppercase ${today ? "text-blue-600" : "text-gray-400"}`}>{WEEKDAYS[w.dow]}</div>
              <div className={`mx-auto mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${today ? "bg-blue-600 text-white" : "text-gray-700"}`}>{w.dom}</div>
            </button>
          );
        })}
      </div>
      {/* All-day strip */}
      <div className="grid border-b border-black/[0.06]" style={{ gridTemplateColumns: cols }}>
        <div className="pr-1 pt-1 text-right text-[9px] uppercase text-gray-400">all-day</div>
        {weekDates.map((w) => {
          const items = eventsOn(w.iso).filter((e) => !inGrid(e));
          const tks = tasksOn(w.iso);
          return (
            <div key={w.iso} onClick={() => onPick(w.iso)} className={`min-h-[40px] cursor-pointer space-y-0.5 border-l border-black/[0.06] p-1 ${w.iso === selectedIso ? "bg-blue-50/30" : ""}`}>
              {items.map((e) => (
                <button key={e.id} onClick={(ev) => { ev.stopPropagation(); onEvent(e); }} className="flex w-full items-center gap-1 truncate rounded-md bg-white px-1.5 py-1 text-left shadow-sm ring-1 ring-black/[0.05]">
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TYPE_CONFIG[e.type].dot}`} />
                  <span className="truncate text-[10px] text-gray-700">{e.title}</span>
                </button>
              ))}
              {/* AMB-409: an all-day task opens the task in the Tasks app (was a dead div
                  that only bubbled to the day-select). */}
              {tks.map((t) => (
                <Link
                  key={t.id}
                  to={t.href}
                  onClick={(ev) => ev.stopPropagation()}
                  className="flex items-center gap-1.5 truncate rounded-md bg-white px-1.5 py-1 shadow-sm ring-1 ring-black/[0.05] transition-all duration-150 hover:bg-gray-50 hover:ring-black/[0.1]"
                >
                  <OwnerAvatar owner={t.owner} sz="h-4 w-4 text-[7px]" />
                  <span className="truncate text-[10px] text-gray-700">☐ {t.title}</span>
                </Link>
              ))}
            </div>
          );
        })}
      </div>
      {/* Hour rows */}
      <div className="max-h-[440px] overflow-y-auto">
        {GRID_HOURS.map((h) => (
          <div key={h} className="grid" style={{ gridTemplateColumns: cols }}>
            <div className="border-r border-black/[0.06] pr-1 pt-1 text-right text-[9px] text-gray-400">{time12(h)}</div>
            {weekDates.map((w) => {
              const evs = eventsOn(w.iso).filter((e) => inGrid(e) && e.hour === h);
              return (
                <div key={w.iso} onClick={() => onPick(w.iso)} className={`min-h-[44px] cursor-pointer border-b border-l border-black/[0.06] p-0.5 ${w.iso === selectedIso ? "bg-blue-50/20" : "hover:bg-gray-50/60"}`}>
                  {evs.map((e) => (
                    <button key={e.id} onClick={(ev) => { ev.stopPropagation(); onEvent(e); }} className={`mb-0.5 block w-full truncate rounded-md ${TYPE_CONFIG[e.type].dot} px-1.5 py-1 text-left text-[10px] font-medium text-white`}>{e.title}</button>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// Agenda — Apple-style vertical list grouped by day; events + tasks inline (layout C).
function AgendaView({ monthPrefix, monthLabel, events, tasks, onEvent }: { monthPrefix: string; monthLabel: string; events: CalEvent[]; tasks: CalTask[]; onEvent: (e: CalEvent) => void }) {
  const dates = useMemo(() => {
    const set = new Set<string>();
    for (const e of events) if (e.date.startsWith(monthPrefix)) set.add(e.date);
    for (const t of tasks) if (t.date.startsWith(monthPrefix)) set.add(t.date);
    return [...set].sort().reverse(); // most-recent day first (reverse chronological)
  }, [events, tasks, monthPrefix]);

  return (
    <div>
      <h2 className="mb-4 text-sm font-semibold text-gray-900">Agenda — {monthLabel}</h2>
      <div className="space-y-5">
        {dates.length === 0 && <p className="text-xs text-gray-400">Nothing scheduled this month for the current filters</p>}
        {dates.map((iso) => {
          const d = new Date(`${iso}T00:00:00`);
          const dom = d.getDate();
          const evs = events.filter((e) => e.date === iso);
          const tks = tasks.filter((t) => t.date === iso);
          const count = evs.length + tks.length;
          return (
            <div key={iso}>
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600">{dom}</span>
                <span className="text-[13px] font-semibold text-gray-700">{d.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric" })}</span>
                <span className="text-xs text-gray-400">{count} item{count !== 1 ? "s" : ""}</span>
              </div>
              <div className="ml-9 space-y-2">
                {evs.map((e) => (
                  <button key={e.id} onClick={() => onEvent(e)} className="flex w-full items-center gap-3 rounded-lg bg-white p-3 text-left shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:shadow">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${TYPE_CONFIG[e.type].dot}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium text-gray-900">{e.title}</p>
                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        {e.time && <span>{e.time}</span>}
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${TYPE_CONFIG[e.type].chip}`}>{TYPE_CONFIG[e.type].label}</span>
                      </div>
                    </div>
                    {e.client && e.client !== "internal" && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">{clientCfg(e.client).name}</span>}
                  </button>
                ))}
                {tks.map((t) => {
                  const card = (
                    <div className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:shadow">
                      <OwnerAvatar owner={t.owner} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium text-gray-900">☐ {t.title}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-500">To-do</span>
                          <DiffBars d={t.difficulty} />
                        </div>
                      </div>
                      {t.client && t.client !== "internal" && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">{clientCfg(t.client).name}</span>}
                    </div>
                  );
                  return t.href ? <Link key={t.id} to={t.href} className="block">{card}</Link> : <div key={t.id}>{card}</div>;
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
